import router from 'src/router/index.js'
import { useCounterStore } from 'src/stores/routerProcessing'
import prefixEve from 'src/utils/routeFilter.js'
import { getFirstAccessiblePath } from 'src/utils/routePermission.js'
const prefix = prefixEve.cuttingFiler()
const loginPath = `${prefix}/login`

// 裸前缀入口（/ent、/boss，含尾斜杠）：登录页/免登录中转页成功后的兜底目标，
// 具体落地页由守卫按权限过滤后的路由表动态计算（子用户可能无仪表盘权限，不能写死 dashboard）
const isBarePrefix = (path) => path === prefix || path === `${prefix}/`

// 当前仅支持企业端 /ent 与 BOSS 端 /boss：其它一级路径（如 /ddd）没有对应的登录页
// 与动态路由，若仍跳 `${prefix}/login`，会因该路由不存在而被守卫反复重定向，
// 表现为页面无限刷新，因此这类前缀直接放行，由通配路由渲染 404 页面
const KNOWN_PREFIXES = ['/ent', '/boss']

// 登录页、找回密码页与免登录中转页（兑换 token 前无登录态）直接放行
const WHITE_LIST = ['EnterpriseLogin', 'BossLogin', 'EnterpriseForgot', 'EnterpriseImpersonate', 'EnterpriseAuth']

// 登录态判断：统一 token 为主，兼容分端写入的 token
const getToken = () => {
  return window.localStorage.enterprisetoken || window.localStorage.bosstoken
}

router.beforeEach(async (to, from, next) => {
  // 未知业务端前缀：不做登录跳转，直接命中 /:pathMatch(.*)* 渲染 404 页面
  if (!KNOWN_PREFIXES.includes(prefix)) {
    next()
    return
  }

  if (WHITE_LIST.includes(to.name)) {
    next()
    return
  }

  // 未登录：回到对应端登录页，并带上原地址供登录后跳回
  if (!getToken()) {
    // 根路由 / 与登录页自身无有效目标，不写入 redirect，登录页自然回退工作台首页
    const noRedirect = to.fullPath === loginPath || to.path === '/'
    next({ path: loginPath, query: noRedirect ? {} : { redirect: to.fullPath }, replace: true })
    return
  }

  const counter = useCounterStore()
  // 动态路由只注册一次（以 routesLoaded 标记判断：routerList 可能因用户无任何权限而过滤为空，
  // 用长度判断会把空表当作未注册，每次导航重复请求接口并重定向，形成死循环）
  if (!counter.routesLoaded) {
    await counter.setRoleData()
    counter.routerList.forEach((item) => {
      router.addRoute(item)
    })
    // 注册完成后路由表已完整，重新解析目标：
    // 登录后回跳（from 为登录页）或裸前缀入口场景下，若目标为根路由 / 或仍命中 NotFound 兜底
    // （404 拼错、权限过滤后被剔除的路由，如子用户无仪表盘权限），改跳权限过滤后
    // 第一个可见页面；无任何可见页面（firstPath 为 null）维持 404 兜底，避免重定向死循环
    const fromLogin =
      from.name === 'BossLogin' || from.name === 'EnterpriseLogin' || from.name === 'EnterpriseImpersonate'
    const resolved = router.resolve(to.fullPath)
    const isInvalid = to.path === '/' || resolved.name === 'NotFound'
    // 裸前缀（/boss）的静态 redirect 在匹配阶段已执行，此时 to.path 是 redirect 目标，
    // 需通过 redirectedFrom 识别裸前缀入口
    const bareEntry =
      isBarePrefix(to.path) || Boolean(to.redirectedFrom && isBarePrefix(to.redirectedFrom.path))
    if ((fromLogin || bareEntry) && isInvalid) {
      const firstPath = getFirstAccessiblePath(counter.routerList)
      if (firstPath) {
        next({ path: firstPath, replace: true })
        return
      }
    }
    // 只能按 path 重新导航：此时 to.name 是 NotFound，
    // 若 spread 整个 to，vue-router 的 resolve 会优先按 name 匹配，再次命中 404 记录
    next({ path: to.path, query: to.query, hash: to.hash, replace: true })
    return
  }

  // 路由已注册后的后续导航：裸前缀静态 redirect 指向已被权限剔除的页面时
  // （如子用户无仪表盘权限，/boss → /boss/dashboard/index 命中 NotFound），
  // 同样改跳第一个可访问页；输错地址的直接访问不受此纠正，仍正常显示 404
  if (to.name === 'NotFound' && to.redirectedFrom && isBarePrefix(to.redirectedFrom.path)) {
    const firstPath = getFirstAccessiblePath(counter.routerList)
    if (firstPath) {
      next({ path: firstPath, replace: true })
      return
    }
  }

  next()
})
